import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getBlobUrl } from '../utils/getBlobUrl';
const AudioPlayerContext = createContext();

export const useAudioPlayer = () => {
  return useContext(AudioPlayerContext);
};

export const AudioPlayerProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTrack, setCurrentTrack] = useState(null);
  const { i18n } = useTranslation(); // Get i18n instance here
  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.volume = volume;
      audioRef.current.muted = isMuted;

      audioRef.current.addEventListener('timeupdate', handleTimeUpdate);
      audioRef.current.addEventListener('ended', handleTrackEnded);
      audioRef.current.addEventListener('play', () => setIsPlaying(true));
      audioRef.current.addEventListener('pause', () => setIsPlaying(false));
    }    

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
        audioRef.current.removeEventListener('ended', handleTrackEnded);
        audioRef.current.removeEventListener('play', () => setIsPlaying(true));
        audioRef.current.removeEventListener('pause', () => setIsPlaying(false));
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Update audio element src when currentTrack changes
  useEffect(() => { // This useEffect now handles language-specific audio path resolution
    const resolveAudioPath = async () => {
      if (!audioRef.current || !currentTrack) {
        audioRef.current.pause();
        audioRef.current.src = ''; // Clear source
        setProgress(0);
        setCurrentTime(0);
        setDuration(0);
        return;
      }

      const currentLang = i18n.language || 'en';
      let resolvedAudioUrl = '';

      // Prioritize new audioPaths structure
      if (currentTrack.audioPaths) {
        const langSpecificUrl = currentTrack.audioPaths[currentLang];
        const englishFallbackUrl = currentTrack.audioPaths.en;

        // Check if language-specific file exists
        if (langSpecificUrl) {
          const blobUrl = getBlobUrl(langSpecificUrl);
          try {
            if ((await fetch(blobUrl, { method: 'HEAD' })).ok) {
              resolvedAudioUrl = blobUrl;
            }
          } catch (e) {}
        }

        if (!resolvedAudioUrl && englishFallbackUrl) {
          const blobUrl = getBlobUrl(englishFallbackUrl);
          try {
            if ((await fetch(blobUrl, { method: 'HEAD' })).ok) {
              resolvedAudioUrl = blobUrl;
            }
          } catch (e) {}
        }
      }
      // Fallback to old audio.audioUrl structure if audioPaths not present
      else if (currentTrack.audioUrl) {
        // Attempt to infer language-specific path if not explicitly defined
        if (currentLang !== 'en' && currentTrack.audioUrl.includes('/en/')) {
          const inferredUrl = currentTrack.audioUrl.replace('/en/', `/${currentLang}/`);
          const blobInferredUrl = getBlobUrl(inferredUrl);
          try {
            const response = await fetch(blobInferredUrl, { method: 'HEAD' });
            if (response.ok) {
              resolvedAudioUrl = blobInferredUrl;
            } else {
              resolvedAudioUrl = getBlobUrl(currentTrack.audioUrl);
            }
          } catch (e) {
            resolvedAudioUrl = getBlobUrl(currentTrack.audioUrl);
          }
        } else {
          resolvedAudioUrl = getBlobUrl(currentTrack.audioUrl);
        }
      }

      console.log(`AudioPlayerContext: Resolved audio URL for lang '${currentLang}':`, resolvedAudioUrl);

      if (resolvedAudioUrl) {
        audioRef.current.src = resolvedAudioUrl;
        audioRef.current.load();
        if (isPlaying) {
          const playPromise = audioRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              if (error.name !== 'AbortError') console.error("Audio playback error:", error);
            });
          }
        }
      }
    };

    resolveAudioPath();
  }, [currentTrack, isPlaying, i18n.language]); // Depend on currentTrack, isPlaying, and i18n.language

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current && !isNaN(audioRef.current.duration) && isFinite(audioRef.current.duration)) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration);
      setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
    }
  }, []);

  const handleTrackEnded = useCallback(() => {
    setIsPlaying(false);
    setProgress(0);
  }, []);

  const playTrack = useCallback((track) => {
    console.log('AudioPlayerContext: playTrack called with:', track?.audioUrl);
    if (currentTrack?.audioUrl !== track.audioUrl) {
      setCurrentTrack(track);
    }
    if (track?.audioUrl) {
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
    // audioRef.current.play() is handled by useEffect when src changes
  }, [currentTrack]);

  const pauseTrack = useCallback(() => {
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
    }
  }, []);

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pauseTrack();
    } else {
      if (currentTrack?.audioUrl) {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            if (error.name !== 'AbortError') console.error("Audio playback error:", error);
          });
        }
        setIsPlaying(true);
      }
    }
  }, [isPlaying, pauseTrack, currentTrack]);

  const toggleMute = useCallback(() => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    audioRef.current.muted = newMuted;
  }, [isMuted]);

  const setPlayerVolume = useCallback((newVolume) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      if (newVolume > 0 && isMuted) { // Unmute if volume increases from 0 while muted
        setIsMuted(false);
        audioRef.current.muted = false;
      } else if (newVolume === 0 && !isMuted) { // Mute if volume set to 0 and not already muted
        setIsMuted(true);
        audioRef.current.muted = true;
      }
    }
  }, [isMuted]);

  const seek = useCallback((time) => {
    if (audioRef.current && !isNaN(audioRef.current.duration)) {
      audioRef.current.currentTime = audioRef.current.duration * (time / 100);
    }
  }, []);

  const seekTo = useCallback((time) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  }, []);

  const skip = useCallback((val) => {
    if (audioRef.current) {
      audioRef.current.currentTime += val;
    }
  }, []);

  const value = {
    isPlaying,
    isMuted,
    volume,
    progress,
    currentTime,
    duration,
    currentTrack,
    playTrack,
    pauseTrack,
    togglePlayPause,
    toggleMute,
    setPlayerVolume,
    seek,
    seekTo,
    skip,
    setCurrentTrack,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
};